import React, { useEffect, useRef } from "react";

/**
 * EditableText — contentEditable text with React-friendly value sync.
 * - In view mode (`editable=false`) renders read-only text.
 * - In edit mode the user can click & type. Calls `onCommit(text)` on blur or
 *   on Enter (when `multiline` is false). Plain text only (no rich HTML).
 *
 * Sync strategy: only write the DOM value back from props when the element is
 * NOT focused, which avoids cursor jumps mid-typing.
 */
export default function EditableText({
  value,
  onCommit,
  editable = false,
  multiline = false,
  className = "",
  placeholder = "Click to edit",
  as: Tag = "span",
}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerText !== (value || "")) {
      el.innerText = value || "";
    }
  }, [value]);

  if (!editable) {
    // Render as a normal element so styles match the editable case.
    return <Tag className={className}>{value}</Tag>;
  }

  const commit = (e) => {
    const next = e.currentTarget.innerText;
    if (next !== value) onCommit(next);
  };

  const onKeyDown = (e) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      // restore original value and blur
      if (ref.current) ref.current.innerText = value || "";
      e.currentTarget.blur();
    }
  };

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      onBlur={commit}
      onKeyDown={onKeyDown}
      className={`${className} editable-text outline-none focus:ring-2 focus:ring-[var(--vermillion)]/40 hover:ring-1 hover:ring-[var(--vermillion)]/30 rounded px-1 -mx-1 transition-shadow whitespace-pre-wrap`}
    />
  );
}
